import React, { useContext } from 'react';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { Label } from '@nooz-optics/nooz-components-lib';
import { IButton } from '../../../../../utils/types/contentful';
import { IColor } from '../../../utils/types/contentful';
import {
  productPackState,
  productPackStepOptionState,
  productState,
  variantState,
} from '../../../utils/recoil/product';
import { withSuspense } from '../../../utils/hocs/withSuspense';
import { CheckoutContext } from '../../../utils/providers/checkout';
import { CartContext } from '../../../utils/providers/cart';
import { Cart } from '../Cart/cart';
import { CartFooter } from '../Cart/Footer/cartFooter';
import { pushAddToCartToDataLayer } from '../../../utils/dataLayer/addToCart';
import { formatContentfulComponentProps } from '../../../utils/globals';
import { ProductPackGlobalSelector } from '../Product/ProductMain/Pack/OptionSelector/ProductPackGlobalSelector';
import { ProductPackProgressBar } from '../Product/ProductMain/Pack/OptionSelector/productPackProgressBar';
import { BuyProductPackSelectorButton } from './buyProductSelectorPack';
import { Button } from '../../UI/Elements';
import { useDrawer } from 'utils/recoil/drawer';

export interface BuyProductButton {
  buttons: IButton;
  color: IColor;
  currentVariantSelection: any;
}

const LoadingButton = () => (
  <Button
    color={{ classname: 'primary' }}
    fullWidth
    disabled
    type='contained'>
    <div className='flex justify-center items-center h-full w-full'>
      <svg
        className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'>
        <circle
          className='opacity-25'
          cx='12'
          cy='12'
          r='10'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          className='opacity-75'
          fill='currentColor'
          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
        />
      </svg>
    </div>
  </Button>
);

export const BuyProductPackButton: React.FC<BuyButtonProps> = withSuspense(
  ({
    buttons,
    color,
    shopifyKey,
    productPackOptionList,
    productPackPicker,
  }) => {
    const productPack = useRecoilValue(productPackState);
    const drawer = useDrawer();
    const setProductPack = useSetRecoilState(productPackState);
    const [productPackStepOption, setProductPackStepOption] = useRecoilState(
      productPackStepOptionState
    );
    const resetProduct = useResetRecoilState(productState);
    const resetVariant = useResetRecoilState(variantState);

    const { modelPicker } = formatContentfulComponentProps(productPackPicker);
    const modelPickerData = formatContentfulComponentProps(modelPicker);

    const cart = useContext(CartContext);
    const body = formatContentfulComponentProps(cart.body);
    const footer = formatContentfulComponentProps(cart.footer);
    const { client, checkout, updateCheckout, loading } =
      useContext(CheckoutContext);

    const partiallySelectedVariant = productPack.filter((p) => !p).length > 0;

    const returnBuyButtonColor = !color ? { classname: 'primary' } : color;

    const BaseButton = (props) => (
      <Button
        label={{
          text: buttons?.chooseVariant?.text,
        }}
        fullWidth
        type='contained'
        size='base'
        color={returnBuyButtonColor}
        {...props}
      />
    );

    const handleAddToCartClick = () => {
      // Open Cart sidebar
      drawer.open({
        ...cart,
        body: (
          <Cart
            body={body}
            promotion={cart.promotion}
          />
        ),
        footer: (
          <CartFooter
            body={body}
            footer={footer}
          />
        ),
        placement: 'right',
        width: 'lg:w-screen/2 sm:w-screen-2/3 w-screen-95',
      });

      if (typeof window !== 'undefined' && productPack?.length > 0) {
        const lineItemsToAdd = [];

        const productWithSamePackLength = checkout[0].lineItems?.filter(
          (lineItem) =>
            lineItem.customAttributes?.find((attr) =>
              attr.value.includes(shopifyKey)
            )
        ).length;

        productPack.forEach((p) => {
          if (p) {
            const variantToAdd = p.shopifyVariants[0];
            // Start cart loader
            loading[1](true);

            // Take the first and only selected variant
            const graphqlApiId = window.btoa(variantToAdd.id);
            lineItemsToAdd.push({
              variantId: graphqlApiId,
              quantity: 1,
              customAttributes: [
                {
                  key: 'pack',
                  value:
                    productWithSamePackLength > 0
                      ? `${shopifyKey}-${productWithSamePackLength + 1}`
                      : shopifyKey,
                },
              ],
            });

            // Push product to data layer add to cart event
            pushAddToCartToDataLayer(variantToAdd);
          }
        });

        // Add line item to the shopify checkout
        client.checkout
          .addLineItems(checkout[0].id, lineItemsToAdd)
          .then((newCheckout) => {
            // Update Checkout with updated checkout object
            updateCheckout(newCheckout);
            // Initialise product pack state from the list of options
            setProductPack(productPackOptionList.map(() => null));
            // Initialise product pack step state from the list of options to 1
            setProductPackStepOption(productPackOptionList.map(() => 1));
          });
      }
    };

    const handleChooseVariant = () => {
      let optionToChooseIndex;
      // Check for empty option index
      for (let i = 0; i < productPack.length; i++) {
        const option = productPack[i];
        if (option === null) {
          optionToChooseIndex = i;
          break;
        }
      }

      const selectedProduct = productPack[optionToChooseIndex];
      const { products } = productPackOptionList[optionToChooseIndex];
      const { button, category, productMainDetails } =
        formatContentfulComponentProps(
          productPackOptionList[optionToChooseIndex].component
        );

      const hanldeOptionDelete = () => {
        resetProduct();
        resetVariant();

        const productPackOptionStep = [...productPackStepOption];
        productPackOptionStep[optionToChooseIndex] = 1;
        setProductPackStepOption(productPackOptionStep);

        const newProductPack = [...productPack];
        // Update product pack at specified index
        newProductPack[optionToChooseIndex] = null;
        setProductPack(newProductPack);
      };

      const handleCloseSlideOverClick = () => {
        resetProduct();
        resetVariant();
        const productPackOptionStep = [...productPackStepOption];
        productPackOptionStep[optionToChooseIndex] =
          selectedProduct !== null ? 2 : 1;
        if (
          productPackOptionStep[optionToChooseIndex] !==
          productPackStepOption[optionToChooseIndex]
        ) {
          setProductPackStepOption(productPackOptionStep);
        }
      };

      // Open pack selector at good index
      drawer.open({
        body: (
          <ProductPackGlobalSelector
            index={optionToChooseIndex}
            button={button}
            products={products}
            modelPickerData={modelPickerData}
            category={category}
            productMainDetails={productMainDetails}
            hanldeOptionDelete={hanldeOptionDelete}
          />
        ),
        subHeader: (
          <ProductPackProgressBar
            idx={optionToChooseIndex}
            hanldeOptionDelete={hanldeOptionDelete}
          />
        ),
        footer: (
          <div className='w-full p-4 flex justify-center'>
            <BuyProductPackSelectorButton
              index={optionToChooseIndex}
              buttons={formatContentfulComponentProps(
                modelPickerData.addToCartButtons
              )}
            />
          </div>
        ),
        title: modelPickerData.label,
        placement: 'right',
        width: 'lg:w-screen/2 w-screen-95',
        onClose: () => handleCloseSlideOverClick(selectedProduct),
      });
    };

    const returnBuyButtonComponent = () => {
      if (partiallySelectedVariant) {
        // Has more than one variant: can't add to cart
        return (
          <BaseButton
            icon={{ iconKey: 'HiChevronRight', color: 'white' }}
            onClick={handleChooseVariant}
          />
        );
      } else {
        return (
          <BaseButton
            label={{
              text: buttons?.addToCartButton?.text,
            }}
            icon={{ iconKey: 'HiChevronRight', color: 'white' }}
            onClick={handleAddToCartClick}
          />
        );
      }
    };

    return (
      <>
        {returnBuyButtonComponent()}
        <div className='w-full flex justify-center mt-1'>
          <Label
            {...buttons.subLabel}
            size='xs'
          />
        </div>
      </>
    );
  },
  <LoadingButton />
);
